import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { TENANT_IDS, tenantAllPrompts } from '../Copilot/cst/constants/samplePrompts';

// Types for API requests/responses

// Web App Context for the /Ask endpoint
export interface WebAppContext {
    viewId?: string;
    tenantId?: string;
    pageType?: string;
    raidId?: number;
    isTenantScoped?: boolean;
}

// User authorization context from usercontext API
export interface UserAuthorization {
    TenantAuthorizations?: any;
    isUserInOrg?: string;
}

// Additional context wrapper
export interface AdditionalContext {
    webAppContext?: WebAppContext;
    userAuthorization?: UserAuthorization;
}

// Attachment types for grounding
export type AttachmentType = 'RAIDItem' | 'GraphUser' | 'Customer' | 'View';

// Custom dimensions for attachments
export interface AttachmentCustomDimensions {
    tenantId?: string;
    [key: string]: string | undefined;
}

// Attachment structure
export interface AskAttachment {
    type: AttachmentType;
    key: string;
    viewId?: string;  // Optional viewId for View attachments
    customDimensions?: AttachmentCustomDimensions;
}

export interface AskQuestionRequest {
    question: string;
    requestedPlatform: string;
    conversationId: string;
    additionalContext?: AdditionalContext;
    attachments?: AskAttachment[];
}

// Reference type for issues/items in responses
export interface AskReference {
    id: string;
    type: 'issue' | 'action' | 'customer' | 'sharepoint' | 'word' | string;
    title: string;
}

// Action button types
export type ActionButtonType = 'navigation' | 'command';
export type ActionButtonAppearance = 'primary' | 'secondary' | 'subtle';
export type ActionButtonCommand =
    | 'create-action'
    | 'open-dialog'
    | 'refresh-view'
    | 'export-data'
    | string; // Extensible for future commands

// Analytics metadata for action buttons
export interface ActionButtonMetadata {
    /** Category for grouping analytics */
    category?: string;
    /** Additional tracking properties */
    trackingProperties?: Record<string, string | number | boolean>;
    /** Source/context of the button */
    source?: string;
}

// Main action button interface
export interface ActionButton {
    /** Unique identifier for the button */
    id: string;
    /** Display text for the button */
    text: string;
    /** Type of action: navigation or command */
    type: ActionButtonType;
    /** Visual appearance/priority */
    appearance?: ActionButtonAppearance; // Defaults to 'secondary'
    /** Icon name from Fluent UI icons (optional) */
    icon?: string;
    /** For navigation type: URI to navigate to */
    uri?: string;
    /** For navigation type: Whether to open in new tab */
    openInNewTab?: boolean;
    /** For command type: Command identifier */
    command?: ActionButtonCommand;
    /** For command type: Parameters to pass to command handler */
    commandParams?: Record<string, unknown>;
    /** Analytics metadata */
    metadata?: ActionButtonMetadata;
    /** Whether button is disabled */
    disabled?: boolean;
    /** Tooltip text (optional, defaults to button text) */
    tooltip?: string;
}

// Content item within a message
export interface AskMessageContent {
    $type: 'text' | string;
    text: string;
}

// Message within the API response
export interface AskResponseMessage {
    role: 'assistant' | 'user' | string;
    /** @deprecated Use text property instead - kept for backwards compatibility */
    contents?: AskMessageContent[];
    /** Direct text content (new format) */
    text?: string;
    /** Message timestamp */
    timestamp?: string;
    references?: AskReference[];
}

export interface AskQuestionResponse {
    id: string;
    conversationId: string;
    timestamp: string;
    messages: AskResponseMessage[];
    citations?: Array<{
        title: string;
        url: string;
        snippet?: string;
    }>;
    actionButtons?: ActionButton[];
    references?: AskReference[];
}

export interface CreateConversationRequest {
    title?: string;
}

export interface CreateConversationResponse {
    conversationId: string;
}

export interface SeedingQuestion {
    id: string;
    question: string;
    category?: string;
    tenantId?: string;
}

export interface GetSuggestionsRequest {
    requestedPlatform: string;
}

// Helper to get GPT base URL from CommonServicesWebApiUrl
const getBaseUrlLegacy = (): string => {
    return `${UATConstants.CommonServicesWebApiUrl}/api/gpt`;
};

// Mock response for "How to create action" prompts
const createMockCreateActionResponse = (question: string, conversationId: string): AskQuestionResponse => {
    return {
        id: `mock-${Date.now()}`,
        response: `Here's how to create a new action for {tenant}:\n\n**Steps to Create an Action:**\n\n1. **Navigate to Actions**: Go to the Actions section in the navigation menu\n2. **Click Create**: Use the "Create New Action" button\n3. **Fill Required Fields**: \n   - Action Title (required)\n   - Description (required)\n   - Priority Level\n   - Due Date\n   - Assignee\n4. **Add Details**: Include any relevant context, attachments, or dependencies\n5. **Save**: Click "Save" to create the action\n\n**Tips:**\n- Use clear, actionable titles\n- Include detailed descriptions for better tracking\n- Set realistic due dates\n- Assign to appropriate team members\n\nWould you like me to help you create a new action now?`,
        conversationId,
        timestamp: new Date().toISOString(),
        messages: [
            {
                text: `Here's how to create a new action for {tenant}:\n\n**Steps to Create an Action:**\n\n1. **Navigate to Actions**: Go to the Actions section in the navigation menu\n2. **Click Create**: Use the "Create New Action" button\n3. **Fill Required Fields**: \n   - Action Title (required)\n   - Description (required)\n   - Priority Level\n   - Due Date\n   - Assignee\n4. **Add Details**: Include any relevant context, attachments, or dependencies\n5. **Save**: Click "Save" to create the action\n\n**Tips:**\n- Use clear, actionable titles\n- Include detailed descriptions for better tracking\n- Set realistic due dates\n- Assign to appropriate team members\n\nWould you like me to help you create a new action now?`,
                references: [],
            },
        ],
        references: [],
        actionButtons: [
            {
                id: 'create-action-btn',
                text: 'Create Action',
                type: 'command' as ActionButtonType,
                appearance: 'primary' as ActionButtonAppearance,
                command: 'create-action',
                commandParams: {
                    source: 'how-to-guide',
                    prefillTitle: '',
                },
                tooltip: 'Create a new action now',
                metadata: {
                    category: 'action-creation',
                    source: 'mock-create-action-response',
                    trackingProperties: {
                        actionType: 'create-new-action',
                        trigger: 'how-to-guide',
                    },
                },
            }
        ],
    };
};

// Mock response for "Open Action" prompts
const createMockOpenActionResponse = (question: string, conversationId: string): AskQuestionResponse => {
    // Extract action identifier from the question (e.g., "Open Action Test" -> "Test")
    const actionId = '13282';

    return {
        id: `mock-${Date.now()}`,
        response: ` **Found Action ${actionId}** would you like to open it?\n\n**Action Details:**\n- **ID:** ${actionId}\n- **Title:** Sample Action for Testing\n- **Status:** Active\n- **Priority:** High\n- **Assigned To:** Current User\n\nThis action has been successfully opened. You can now view all details, make updates, or perform other actions on this item.\n\nThis is a mock response for demonstration purposes.`,
        conversationId,
        timestamp: new Date().toISOString(),
        messages: [
            {
                text: `Found **Action ${actionId}** would you like to open it?\n\n**Action Details:**\n- **ID:** ${actionId}\n- **Title:** Sample Action for Testing\n- **Status:** Active\n- **Priority:** High\n- **Assigned To:** Current User\n\nThis action has been successfully opened. You can now view all details, make updates, or perform other actions on this item.\n\nThis is a mock response for demonstration purposes.`,
                references: [],
            },
        ],
        actionButtons: [
            {
                id: 'edit-action-btn',
                text: `Open Action ${actionId}`,
                type: 'navigation' as ActionButtonType,
                appearance: 'primary' as ActionButtonAppearance,
                uri: `#/action/${actionId}/edit`,
                openInNewTab: false,
                tooltip: `Edit Action ${actionId}`,
                metadata: {
                    category: 'navigation',
                    source: 'mock-open-action-response',
                    trackingProperties: {
                        actionId: actionId,
                        actionType: 'edit-action',
                    },
                },
            },
        ],
    };
};

// Mock response for "Open" prompts
const createMockOpenResponse = (question: string, conversationId: string): AskQuestionResponse => {
    // Extract the entity name from the question (e.g., "Open Ryan Moore" -> "Ryan Moore")
    const entityMatch = question.match(/^Open\s+(.+)/i);
    const entityName = entityMatch ? entityMatch[1].trim() : 'the item';

    return {
        id: `mock-${Date.now()}`,
        response: `Opening **${entityName}**...\n\nThis is a mock response for demonstration purposes. In the real implementation, this would navigate to or open the requested item.`,
        conversationId,
        timestamp: new Date().toISOString(),
        messages: [
            {
                text: `Opening **${entityName}**...\n\nThis is a mock response for demonstration purposes. In the real implementation, this would navigate to or open the requested item.`,
                references: [],
            },
        ],
        references: [],
        actionButtons: [
            {
                id: 'open-action',
                text: `View ${entityName}`,
                type: 'navigation' as ActionButtonType,
                appearance: 'primary' as ActionButtonAppearance,
                uri: '#',
                metadata: {
                    category: 'navigation',
                    source: 'mock-response',
                },
            },
        ],
    };
};

// Mock response for "Summarize Action" prompts
const createMockSummarizeActionResponse = (question: string, conversationId: string): AskQuestionResponse => {
    // Hardcoded action identifier for "summarize action test"
    const actionId = 'test';

    return {
        id: `mock-${Date.now()}`,
        response: `Here's a summary of **Action ${actionId}**:\n\n**Title:** Implement new authentication flow\n\n**Status:** In Progress\n\n**Owner:** John Doe\n\n**Description:** This action involves implementing a new authentication flow with multi-factor authentication support. The work is currently 60% complete with the backend integration done and frontend work in progress.\n\n**Key Points:**\n- Backend API endpoints completed\n- MFA integration with Azure AD done\n- Frontend UI implementation ongoing\n- Testing scheduled for next sprint\n\nThis is a mock response for demonstration purposes.`,
        conversationId,
        timestamp: new Date().toISOString(),
        messages: [
            {
                text: `Here's a summary of **Action ${actionId}**:\n\n**Title:** Implement new authentication flow\n\n**Status:** In Progress\n\n**Owner:** John Doe\n\n**Description:** This action involves implementing a new authentication flow with multi-factor authentication support. The work is currently 60% complete with the backend integration done and frontend work in progress.\n\n**Key Points:**\n- Backend API endpoints completed\n- MFA integration with Azure AD done\n- Frontend UI implementation ongoing\n- Testing scheduled for next sprint\n\nThis is a mock response for demonstration purposes.`,
                references: [],
            },
        ],
        references: [
            {
                id: actionId,
                type: 'action',
                title: `Action ${actionId}: Implement new authentication flow`,
            },
        ],
        actionButtons: [
            {
                id: 'open-action-btn',
                text: `Open Action ${actionId}`,
                type: 'navigation' as ActionButtonType,
                appearance: 'primary' as ActionButtonAppearance,
                uri: `#/action/${actionId}`,
                openInNewTab: false,
                tooltip: `View full details of Action ${actionId}`,
                metadata: {
                    category: 'navigation',
                    source: 'mock-summarize-response',
                    trackingProperties: {
                        actionId: actionId,
                        actionType: 'view-action',
                    },
                },
            },
            {
                id: 'create-action-btn',
                text: 'Create Similar Action',
                type: 'command' as ActionButtonType,
                appearance: 'secondary' as ActionButtonAppearance,
                command: 'create-action',
                commandParams: {
                    templateActionId: actionId,
                    prefillTitle: 'New Action based on template',
                },
                tooltip: 'Create a new action using this as a template',
                metadata: {
                    category: 'action-creation',
                    source: 'mock-summarize-response',
                    trackingProperties: {
                        sourceActionId: actionId,
                        actionType: 'create-from-template',
                    },
                },
            },
        ],
    };
};

// Helper to get GPT base URL from CommonServicesWebApiUrl
const getBaseUrl = (): string => {
    return `${UATConstants.CommonServicesWebApiUrl}/api/v1`;
};

// RTK Query API definition
export const copilotApi = createApi({
    reducerPath: 'copilotV2Api',
    baseQuery: fetchBaseQuery({
        baseUrl: '', // Base URL is dynamic, set per endpoint
        prepareHeaders: (headers) => {
            const token = sessionStorage.getItem('jwtTokenForApi');
            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }
            headers.set('Content-Type', 'application/json');

            return headers;
        },
    }),
    tagTypes: ['Conversation', 'Suggestions'],
    endpoints: (builder) => ({
        // POST /conversation/{conversationId}/ask
        askQuestion: builder.mutation<AskQuestionResponse, AskQuestionRequest>({
            queryFn: async ({ question, conversationId, additionalContext, attachments }, queryApi, _extraOptions, baseQuery) => {
                // Read tenant authorization and isUserInOrg from user context in the store
                const state = queryApi.getState() as any;
                const TenantAuthorizations = state?.userSlice?.userProfile?.authorization?.TenantAuthorizations;
                const isUserInOrg = state?.userSlice?.userProfile?.isUserInOrg;

                // Inject userAuthorization into additionalContext
                let enrichedAdditionalContext = additionalContext;
                if (TenantAuthorizations || isUserInOrg) {
                    enrichedAdditionalContext = {
                        ...additionalContext,
                        userAuthorization: {
                            ...(TenantAuthorizations && { TenantAuthorizations }),
                            ...(isUserInOrg && { isUserInOrg }),
                        },
                    };
                }
                // Check if this is a "How to create action" prompt - return mock response
                if (question.trim().toLowerCase() === 'how to create action') {
                    // Simulate a small delay for realistic feel
                    await new Promise(resolve => setTimeout(resolve, 700));
                    return { data: createMockCreateActionResponse(question, conversationId) };
                }

                // Check if this is an "Open Action" prompt - return mock response
                if (question.trim().toLowerCase() === 'open action test') {
                    // Simulate a small delay for realistic feel
                    await new Promise(resolve => setTimeout(resolve, 600));
                    return { data: createMockOpenActionResponse(question, conversationId) };
                }

                // Check if this is an "Open" prompt - return mock response
                if (question.trim().toLowerCase().startsWith('open action')) {
                    // Simulate a small delay for realistic feel
                    await new Promise(resolve => setTimeout(resolve, 500));
                    return { data: createMockOpenActionResponse(question, conversationId) };
                }

                // Check if this is a "Summarize Action" prompt - return mock response with action buttons
                if (question.trim().toLowerCase() === 'summarize action test') {
                    // Simulate a small delay for realistic feel
                    await new Promise(resolve => setTimeout(resolve, 800));
                    return { data: createMockSummarizeActionResponse(question, conversationId) };
                }

                // Otherwise, make the real API call
                const baseUrl = getBaseUrl();
                const result = await baseQuery({
                    url: `${baseUrl}/conversation/${conversationId}/ask`,
                    method: 'POST',
                    body: {
                        content: question,
                        ...(enrichedAdditionalContext && { additionalContext: enrichedAdditionalContext }),
                        ...(attachments && attachments.length > 0 && { attachments }),
                    },
                });

                if (result.error) {
                    return { error: result.error };
                }

                const response = result.data as any;

                // Extract action buttons if present
                let actionButtons: ActionButton[] | undefined;
                if (response.actionButtons && Array.isArray(response.actionButtons)) {
                    actionButtons = response.actionButtons.map((button: ActionButton & { verb?: string }) => {
                        // Handle new format with type field
                        if (button.type) {
                            return {
                                id: button.id || '',
                                text: button.text || '',
                                type: button.type,
                                appearance: button.appearance || 'secondary',
                                icon: button.icon,
                                uri: button.uri,
                                openInNewTab: button.openInNewTab,
                                command: button.command,
                                commandParams: button.commandParams,
                                metadata: button.metadata,
                                disabled: button.disabled,
                                tooltip: button.tooltip,
                            };
                        }
                        // Backward compatibility: convert old format (with verb) to new format
                        return {
                            id: button.id || '',
                            text: button.text || '',
                            type: 'navigation' as ActionButtonType,
                            appearance: 'secondary' as ActionButtonAppearance,
                            uri: button.uri || '',
                        };
                    });
                }

                // Handle new messages array format from /ask API
                let responseText = '';
                let references: any[] = [];
                let messages: any[] = [];

                if (response.messages && Array.isArray(response.messages) && response.messages.length > 0) {
                    // New format: extract from messages array
                    messages = response.messages;
                    // Combine all message texts
                    responseText = response.messages.map((msg: any) => msg.text || '').join('\n\n');
                    // Collect all references from messages
                    references = response.messages.flatMap((msg: any) => msg.references || []);
                } else {
                    // Legacy format fallback
                    responseText = response.response || response.answer || response.text || '';
                    references = response.citations || [];
                }

                return {
                    data: {
                        id: response.id || Date.now().toString(),
                        response: responseText,
                        conversationId: conversationId,
                        timestamp: new Date().toISOString(),
                        citations: response.citations,
                        actionButtons,
                        messages,
                        references,
                    },
                };
            },
            invalidatesTags: ['Conversation'],
        }),

        // POST /conversation
        createConversation: builder.mutation<CreateConversationResponse, CreateConversationRequest>({
            query: ({ title = 'New Conversation' }) => {
                const baseUrl = getBaseUrl();

                return {
                    url: `${baseUrl}/conversation`,
                    method: 'POST',
                    body: {
                        Content: title,
                    },
                };
            },
            transformResponse: (response: any) => {
                if (!response.conversationId) {
                    throw new Error('Conversation created but no ID returned');
                }

                return {
                    conversationId: response.conversationId,
                };
            },
            invalidatesTags: ['Conversation'],
        }),

        // GET /gpt/seedingquestions - Returns tenant-organized suggestions since API endpoint is not available
        getSuggestions: builder.query<SeedingQuestion[], GetSuggestionsRequest>({
            queryFn: async () => {
                // Generate SeedingQuestion[] from tenant-organized prompts in samplePrompts.ts
                const suggestions: SeedingQuestion[] = [];
                let index = 0;

                // Iterate over all tenants and their prompts
                for (const [tenantKey, tenantId] of Object.entries(TENANT_IDS)) {
                    const prompts = tenantAllPrompts[tenantId as keyof typeof tenantAllPrompts];
                    if (prompts) {
                        for (const prompt of prompts) {
                            index++;
                            suggestions.push({
                                id: `${tenantKey.toLowerCase()}-${index}`,
                                question: prompt,
                                category: 'NL2SQL',
                                tenantId,
                            });
                        }
                    }
                }

                return { data: suggestions };
            },
            // providesTags: ['Suggestions'],
            // Cache for 30 minutes
            keepUnusedDataFor: 1800,
        }),
    }),
});

// Export hooks for usage in components
export const {
    useAskQuestionMutation,
    useCreateConversationMutation,
    useGetSuggestionsQuery,
    useLazyGetSuggestionsQuery,
} = copilotApi;

// Generated by GitHub Copilot | Updated on: 2026-04-01 | 25 lines modified | Added tenantId to SeedingQuestion; replaced mock suggestions with tenant-organized prompts from samplePrompts
